import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Drawer, Empty, Skeleton, Typography } from "antd";
import {
    AuditOutlined,
    BranchesOutlined,
    ClockCircleOutlined,
    FileSearchOutlined,
    MailOutlined,
    PaperClipOutlined,
    ReloadOutlined,
    TeamOutlined,
} from "@ant-design/icons";

import { getEntityDigest } from "../../../../api/v3/mailHubApi";
import { formatContractError, requireRecord } from "../models/contractValidation";
import { buildEntityEmailDigestViewModel } from "../models/entityEmailDigestViewModel";
import MailReadingSurface from "./MailReadingSurface";
import "./EntityEmailPanel.css";

const { Text, Title } = Typography;
const DEFAULT_PAGE_SIZE = 10;

function extract(response) {
    return response?.data?.data;
}

function extractErrorMessage(error, fallback) {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
        return "当前账号没有查看该业务对象邮件上下文的权限。";
    }
    if (status === 404) {
        return "业务对象邮件摘要资源不存在，无法确认当前对象是否没有关联邮件。";
    }
    if (status === 502 || status === 504) {
        return "邮件上下文服务暂时不可用，请稍后重试。";
    }
    return fallback;
}

function SummaryMetric({ label, value, tone = "neutral" }) {
    return (
        <div className={`entity-email-metric entity-email-metric-${tone}`}>
            <span className="entity-email-metric-value">{value}</span>
            <span className="entity-email-metric-label">{label}</span>
        </div>
    );
}

function StatusPill({ status }) {
    return <span className={`entity-email-status entity-email-status-${status.tone || "neutral"}`}>{status.label}</span>;
}

function ThreadCard({ thread, onOpen }) {
    return (
        <button
            type="button"
            className="entity-email-thread-card"
            disabled={!thread.canOpen}
            onClick={() => onOpen(thread)}
        >
            <span className="entity-email-thread-main">
                <span className="entity-email-thread-heading">
                    <span className="entity-email-thread-title">{thread.subject}</span>
                    <StatusPill status={thread.status} />
                </span>

                <span className="entity-email-thread-participants">
                    <TeamOutlined aria-hidden="true" />
                    <span>{thread.participantText}</span>
                    {thread.participantOverflowCount > 0 ? <span>等 {thread.participantOverflowCount + 3} 方</span> : null}
                </span>

                {thread.latestSummary ? (
                    <span className="entity-email-thread-summary">{thread.latestSummary}</span>
                ) : (
                    <span className="entity-email-thread-summary entity-email-muted">暂无摘要</span>
                )}

                <span className="entity-email-thread-meta-row">
                    <span><ClockCircleOutlined aria-hidden="true" />{thread.timeRangeText}</span>
                    <span><MailOutlined aria-hidden="true" />{thread.messageCount} 封邮件</span>
                    <span><PaperClipOutlined aria-hidden="true" />{thread.evidence.label}</span>
                </span>

                <span className="entity-email-thread-category-row">
                    {thread.categories.map((category) => <span key={category} className="entity-email-category-chip">{category}</span>)}
                </span>
            </span>
        </button>
    );
}

export default function EntityEmailPanel({ entityType, entityId }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [payload, setPayload] = useState(null);
    const [activeThread, setActiveThread] = useState(null);

    const canLoad = Boolean(entityType && entityId);

    const focusEntity = useMemo(() => ({
        entityType,
        entityId,
    }), [entityId, entityType]);

    const loadDigest = useCallback(async () => {
        if (!canLoad) {
            setPayload(null);
            setError("");
            return;
        }

        setLoading(true);
        setError("");
        try {
            const response = await getEntityDigest(entityType, entityId, {
                include_threads: true,
                include_work_items: true,
                page: 1,
                page_size: DEFAULT_PAGE_SIZE,
            });
            setPayload(requireRecord(extract(response), "entityDigest"));
        } catch (requestError) {
            setPayload(null);
            setError(formatContractError(requestError, extractErrorMessage(requestError, "加载关联邮件摘要失败")));
        } finally {
            setLoading(false);
        }
    }, [canLoad, entityId, entityType]);

    useEffect(() => {
        loadDigest();
    }, [loadDigest]);

    const viewModelState = useMemo(() => {
        if (!payload) return { contractError: "", viewModel: null };
        try {
            return { contractError: "", viewModel: buildEntityEmailDigestViewModel(payload) };
        } catch (contractError) {
            return {
                contractError: formatContractError(contractError, "关联邮件摘要契约错误"),
                viewModel: null,
            };
        }
    }, [payload]);
    const viewModel = viewModelState.viewModel;
    const firstPendingThread = useMemo(
        () => viewModel?.threads.find((thread) => thread.status.tone === "warning" && thread.canOpen) || null,
        [viewModel?.threads],
    );

    if (!canLoad) {
        return (
            <section className="entity-email-panel">
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="请选择业务对象后查看关联邮件" />
            </section>
        );
    }

    if (loading && !payload) {
        return (
            <section className="entity-email-panel">
                <Skeleton active paragraph={{ rows: 8 }} />
            </section>
        );
    }

    if (error) {
        return (
            <section className="entity-email-panel">
                <Alert
                    type="error"
                    showIcon
                    message="关联邮件摘要加载失败"
                    description={error}
                    action={<Button size="small" icon={<ReloadOutlined />} onClick={loadDigest}>重试</Button>}
                />
            </section>
        );
    }

    if (viewModelState.contractError) {
        return (
            <section className="entity-email-panel">
                <Alert
                    type="error"
                    showIcon
                    message="关联邮件摘要契约错误"
                    description={viewModelState.contractError}
                    action={<Button size="small" icon={<ReloadOutlined />} onClick={loadDigest}>重试</Button>}
                />
            </section>
        );
    }

    if (!viewModel) {
        return (
            <section className="entity-email-panel">
                <Skeleton active paragraph={{ rows: 8 }} />
            </section>
        );
    }

    return (
        <section className="entity-email-panel" aria-label="业务对象邮件上下文面板">
            <header className="entity-email-header">
                <div>
                    <div className="entity-email-kicker"><MailOutlined />邮件上下文</div>
                    <Title level={4} className="entity-email-title">当前业务对象关联邮件</Title>
                </div>
                <Button icon={<ReloadOutlined />} onClick={loadDigest} loading={loading}>刷新</Button>
            </header>

            <div className="entity-email-overview" aria-label="关联概览">
                <SummaryMetric label="相关会话" value={viewModel.summary.totalThreads} tone="accent" />
                <SummaryMetric label="已确认" value={viewModel.summary.confirmedCount} tone="success" />
                <SummaryMetric label="待审核" value={viewModel.summary.pendingReviewCount} tone="warning" />
                <SummaryMetric label="冲突" value={viewModel.summary.conflictAvailable ? viewModel.summary.conflictCount : "暂未返回"} tone="danger" />
            </div>

            {viewModel.summary.totalThreads > 0 ? (
                <div className="entity-email-update-row">
                    <Text type="secondary">最近更新时间：{viewModel.summary.latestUpdated.absoluteText}</Text>
                    <Text type="secondary">邮件跨度：{viewModel.summary.dateRangeText}</Text>
                </div>
            ) : null}

            {viewModel.categoryChips.length > 0 ? (
                <div className="entity-email-category-row" aria-label="分类概览">
                    {viewModel.categoryChips.map((item) => (
                        <span key={item.key} className="entity-email-category-chip">{item.label} {item.count}</span>
                    ))}
                </div>
            ) : null}

            {viewModel.reviewPrompt.visible ? (
                <div className={`entity-email-prompt${viewModel.reviewPrompt.pendingCount > 0 || viewModel.reviewPrompt.unavailable ? " entity-email-prompt-warning" : ""}`}>
                    <AuditOutlined aria-hidden="true" />
                    <div>
                        <Text strong>{viewModel.reviewPrompt.title}</Text>
                        <Text type="secondary">{viewModel.reviewPrompt.description}</Text>
                    </div>
                    {firstPendingThread ? (
                        <Button size="small" onClick={() => setActiveThread(firstPendingThread)}>查看会话</Button>
                    ) : null}
                </div>
            ) : null}

            {viewModel.evidencePrompt.visible ? (
                <div className="entity-email-prompt">
                    <FileSearchOutlined aria-hidden="true" />
                    <div>
                        <Text strong>证据提示</Text>
                        <Text type="secondary">{viewModel.evidencePrompt.description}</Text>
                    </div>
                </div>
            ) : null}

            <div className="entity-email-section-heading">
                <BranchesOutlined />
                <Text strong>相关会话摘要</Text>
                <Text type="secondary">共 {viewModel.pagination.totalThreads} 个会话</Text>
            </div>

            {viewModel.empty.visible ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={viewModel.empty.message} />
            ) : (
                <div className="entity-email-thread-list">
                    {viewModel.threads.map((thread) => (
                        <ThreadCard key={thread.key} thread={thread} onOpen={setActiveThread} />
                    ))}
                </div>
            )}

            {viewModel.pagination.hasMore ? (
                <Text className="entity-email-more-note" type="secondary">
                    当前仅展示前 {viewModel.pagination.pageSize} 个会话，可通过邮件入口继续细查。
                </Text>
            ) : null}

            <Drawer
                title="关联邮件阅读面"
                placement="right"
                width="min(960px, calc(100vw - 32px))"
                open={Boolean(activeThread)}
                onClose={() => setActiveThread(null)}
                destroyOnHidden
            >
                {activeThread ? (
                    <MailReadingSurface
                        entry="business_object"
                        threadId={activeThread.threadId}
                        receiptId={activeThread.receiptId}
                        focusEntity={focusEntity}
                    />
                ) : null}
            </Drawer>
        </section>
    );
}
